/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import net.sergeych.bipack.decodeFromBipack
import net.sergeych.crypto2.VerifyingPublicKey.Companion.toString
import net.sergeych.mp_tools.decodeBase64Url

@Deprecated("Use EncryptingPublicKey",ReplaceWith("EncryptingPublicKey"))
typealias PublicKey = EncryptingPublicKey
/**
 * The public for public-key encryption. It encrypts messages that can only be decrypted with corresponding
 * [DecryptingSecretKey].
 */
@Serializable
@SerialName("encp")
class EncryptingPublicKey(override val keyBytes: UByteArray) : UniversalKey(), EncryptingKey {

    override val magic: KeysmagicNumber = KeysmagicNumber.defaultAssymmetric
    @Transient
    override val label: String = "pub"

    /**
     * Create an anonymous message that could be decrypted only with the [DecryptingSecretKey] that corresponds this.
     * Anonymous message uses one-time secret key, the public part of which is included into the
     * [Asymmetric.Message], so the sender could not be identified.
     *
     * __Anonymous encryption is much slower__ as it generates new keys every time, use [encryptMessage]
     * when possible
     *
     * The authentication is used despite the anonymity, and the fact of the successful decryption
     * proves that the message was not altered after creation.
     */
    fun encryptAnonymousMessage(plainData: UByteArray, randomFill: IntRange? = null): Asymmetric.Message =
        encryptMessage(plainData, randomFill = randomFill)

    /**
     * Anonymous encryption, see [encryptAnonymousMessage], to binary data. Sender could not be identified.
     */
    @Suppress("unused")
    override fun encrypt(plainData: UByteArray, randomFill: IntRange?): UByteArray =
        encryptMessage(plainData, randomFill = randomFill).encoded

    override fun encryptWithNonce(plainData: UByteArray, nonce: UByteArray, randomFill: IntRange?): UByteArray =
        encryptMessage(plainData, nonce = nonce, randomFill = randomFill).encoded

    override val nonceBytesLength: Int = Asymmetric.nonceBytesLength

    /**
     * Universal public-key encryption. Note that message authenticity is guaranteed if the decryption is successful
     * whether [senderKey] is provider, the latter only allow to positively identify the sender.
     *
     * @param plainData data to encrypt
     * @param nonce allows specifying exact nonce, default to random (safe)
     * @param senderKey key to authenticate sending party. It is safe and much faster to specify it,
     *      otherwise an anonymous key will be created for each encryption, also safe and anonymous, but slow.
     */
    fun encryptMessage(
        plainData: UByteArray,
        nonce: UByteArray = randomNonce(),
        senderKey: DecryptingSecretKey = newSecretKey(),
        randomFill: IntRange? = null,
    ) = Asymmetric.createMessage(senderKey, this, WithFill.encode(plainData, randomFill), nonce)

    /**
     * Encrypt message using the specified secret key as sender authentication. Recipient, the party having
     * [DecryptingSecretKey] corresponding to this one, will be able to decrypt the message and be sure that [senderKey]
     * was the author and the message was not altered.
     */
    fun encryptMessage(
        plainData: UByteArray,
        senderKey: DecryptingSecretKey,
        randomFill: IntRange? = null,
    ): Asymmetric.Message =
        Asymmetric.createMessage(senderKey, this, WithFill.encode(plainData, randomFill))

    override val id by lazy { KeyId(magic, keyBytes, null, true) }

    companion object {
        /**
         * Parse any known public key text representation, including what [toString] return (for public keys it is
         * possible)
         * @throws IllegalArgumentException the public key isn't recognized
         */
        fun parse(text: String): EncryptingPublicKey {
            val s = text.trim()

            fun parseId(t: String): EncryptingPublicKey {
                val id = BinaryId.restoreFromString(t)
                if (id.magic != KeysmagicNumber.defaultAssymmetric.ordinal)
                    throw IllegalArgumentException("invalid magick ${id.magic} for PublicKey")
                return id.asPublicKey
            }

            // 🗝sig#I1po9Y2I7p2aOxeh4nFyGPm3e0YunBEu1Mo-PmIqP84Evg
            return when {
                s.startsWith("\uD83D\uDDDDpub#") -> parseId(s.drop(6))
                s.startsWith("pub#") -> parseId(s.drop(4))
                s.startsWith("#") -> parseId(s.drop(1))
                else -> {
                    // consider it is serialized key in base64 format
                    val data = s.decodeBase64Url().asUByteArray()
                    if (data.size == 32)
                        EncryptingPublicKey(data)
                    else {
                        runCatching { data.decodeFromBipack<EncryptingPublicKey>() }.getOrNull()
                            ?: kotlin.runCatching { data.decodeFromBipack<UniversalKey>() as EncryptingPublicKey }
                                .getOrElse { throw IllegalArgumentException("can't parse verifying key") }
                    }
                }
            }
        }
    }
}